<!-- Cell templates -->
<ng-template #nameTpl let-person>
  <div class="flex items-center gap-3">
    <div
      class="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-xs font-semibold text-navy-700"
    >
      {{ initials(person) }}
    </div>
    <span class="font-semibold text-navy-900 whitespace-nowrap">
      {{ person.firstName }} {{ person.surnames }}
      @if (tagline(person)) {
        <span class="font-normal text-slate-400">· {{ tagline(person) }}</span>
      }
    </span>
  </div>
</ng-template>

<ng-template #typeTpl let-person>
  <span
    class="inline-flex px-3 py-0.5 rounded-full text-xs font-medium bg-surface-200 text-slate-700 whitespace-nowrap"
  >
    {{ person.personTypeName }}
  </span>
</ng-template>

<!-- Page layout -->
<div class="flex flex-col h-full bg-surface-100 px-4 sm:px-10 py-6 gap-4">
  <!-- Filters -->
  <div class="flex flex-wrap items-end gap-3 py-2">
    <div class="flex flex-col gap-1.5 flex-1 min-w-48 max-w-96">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Search</label>
      <div class="relative">
        <img
          src="/icons/search-icon.svg"
          class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
          alt=""
        />
        <input
          type="text"
          [value]="search()"
          (input)="onSearch($any($event.target).value)"
          placeholder="Filter by name or CIN..."
          class="w-full pl-8 pr-3"
        />
      </div>
    </div>
    <div class="flex flex-col gap-1.5 min-w-44">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Person Type</label>
      <app-select
        placeholder="Select All"
        [options]="typeOptions()"
        [value]="selectedTypeCode()"
        (changed)="onTypeChange($event)"
      />
    </div>
  </div>

  <!-- Table -->
  <div
    class="flex-1 min-h-0 bg-white rounded-xl border border-slate-100 overflow-hidden flex flex-col"
  >
    <app-data-table
      [columns]="columns()"
      [rows]="persons()"
      [pageSize]="pageSize"
      [total]="total()"
      [page]="page()"
      [loading]="loading()"
      [clickableRows]="true"
      (pageChange)="onPageChange($event)"
      (rowAction)="onRowAction($event)"
    />
  </div>
</div>

<!-- Detail / Edit Modal -->
@if (showDetailModal()) {
  <div
    class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    (click)="showDetailModal.set(false)"
  >
    <div
      class="bg-white rounded-3xl w-full max-w-sm shadow-xl flex flex-col max-h-[90vh]"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div class="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
        <div>
          <p class="text-sm font-bold text-navy-900">Person information</p>
        </div>
        <button
          type="button"
          (click)="showDetailModal.set(false)"
          class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer shrink-0 ml-3"
        >
          ✕
        </button>
      </div>

      <!-- Body -->
      @if (detailLoading()) {
        <div class="flex items-center justify-center py-16">
          <div
            class="w-6 h-6 rounded-full border-2 border-accent-500 border-t-transparent animate-spin"
          ></div>
        </div>
      } @else if (detail()) {
        <div class="overflow-y-auto px-6 pb-6 flex flex-col gap-4">
          <!-- Synced persons are read-only; only manual entries can be edited (JCCA-177) -->
          @if (detailReadOnly()) {
            <p
              class="text-xs text-slate-500 bg-slate-50 border border-surface-300 rounded-lg px-3 py-2"
            >
              This record is synced from another system and can't be edited here. Only
              <span class="font-semibold">Manual Entry</span> records can be edited.
            </p>
          }

          <!-- Record Origin: view only (JCCA-178) -->
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Record Origin</label>
            <input
              type="text"
              class="w-full bg-slate-50 text-slate-500 cursor-not-allowed"
              [value]="detail()!.personRecordOriginName ?? '--'"
              disabled
            />
          </div>

          <fieldset
            [disabled]="detailReadOnly()"
            class="flex flex-col gap-4 min-w-0"
            [class.opacity-60]="detailReadOnly()"
            [class.pointer-events-none]="detailReadOnly()"
          >
            <!-- Person Type -->
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-navy-600 tracking-wider">Person Type</label>
              <app-select
                placeholder="Select type…"
                [options]="detailTypeOptions()"
                [value]="dPersonTypeId()"
                (changed)="dPersonTypeId.set($event)"
              />
            </div>

            <!-- First Name + Surnames -->
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-navy-600 tracking-wider">First Name</label>
                <input
                  type="text"
                  class="w-full"
                  [value]="dFirstName()"
                  (input)="dFirstName.set($any($event.target).value)"
                  placeholder="First name…"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-navy-600 tracking-wider">Surnames</label>
                <input
                  type="text"
                  class="w-full"
                  [value]="dSurnames()"
                  (input)="dSurnames.set($any($event.target).value)"
                  placeholder="Surnames…"
                />
              </div>
            </div>

            <!-- Phone -->
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-navy-600 tracking-wider">Phone Number</label>
              <input
                type="tel"
                maxlength="10"
                class="w-full"
                [value]="dPhone()"
                (input)="dPhone.set($any($event.target).value)"
                placeholder="000-000-0000"
              />
            </div>

            <!-- Date of Birth -->
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-navy-600 tracking-wider"
                >Date of Birth</label
              >
              <app-date-picker
                [value]="dDob()"
                [hideTime]="true"
                (valueChange)="dDob.set($event)"
                class="w-full"
              />
            </div>

            <!-- Sex -->
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-semibold text-navy-600 tracking-wider">Sex</label>
              <app-select
                placeholder="Select…"
                [options]="sexOptions"
                [value]="dSex()"
                (changed)="dSex.set($event)"
              />
            </div>

            <!-- Medicaid CIN + HR File Number -->
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-navy-600 tracking-wider"
                  >Medicaid CIN</label
                >
                <input
                  type="text"
                  class="w-full"
                  [value]="dMedicaidCIN()"
                  (input)="dMedicaidCIN.set($any($event.target).value); dCinInUse.set(false)"
                  [class.border-red-400]="dCinInUse()"
                  [class.ring-2]="dCinInUse()"
                  [class.ring-red-100]="dCinInUse()"
                  placeholder="CIN…"
                />
                @if (dCinInUse()) {
                  <p class="text-2xs text-red-500">
                    This CIN is already in use. Check if this record already exists.
                  </p>
                }
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-navy-600 tracking-wider">HR File #</label>
                <input
                  type="text"
                  class="w-full"
                  [value]="dHrFileNumber()"
                  (input)="dHrFileNumber.set($any($event.target).value)"
                  placeholder="HR file…"
                />
              </div>
            </div>
          </fieldset>

          <!-- Synced from the client's systems: read-only (JCCA-163 / JCCA-166) -->
          @if (detail()!.jobTitle || detail()!.adminPayor) {
            <div class="grid grid-cols-2 gap-3">
              @if (detail()!.jobTitle) {
                <div class="flex flex-col gap-1.5">
                  <label class="text-xs font-semibold text-navy-600 tracking-wider"
                    >Job Title</label
                  >
                  <input
                    type="text"
                    class="w-full bg-slate-50 text-slate-500 cursor-not-allowed"
                    [value]="detail()!.jobTitle"
                    disabled
                  />
                </div>
              }
              @if (detail()!.adminPayor) {
                <div class="flex flex-col gap-1.5">
                  <label class="text-xs font-semibold text-navy-600 tracking-wider"
                    >Admin Payor</label
                  >
                  <input
                    type="text"
                    class="w-full bg-slate-50 text-slate-500 cursor-not-allowed"
                    [value]="detail()!.adminPayor"
                    disabled
                  />
                </div>
              }
            </div>
          }

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              (click)="showDetailModal.set(false)"
              class="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
            >
              {{ detailReadOnly() ? 'Close' : 'Cancel' }}
            </button>
            @if (!detailReadOnly()) {
              <button
                type="button"
                (click)="onSaveDetail()"
                [disabled]="detailSaving()"
                class="px-5 py-2 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ detailSaving() ? 'Saving…' : 'Save' }}
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>
}

<!-- Create Modal -->
@if (showCreateModal()) {
  <div
    class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    (click)="showCreateModal.set(false)"
  >
    <div
      class="bg-white rounded-3xl w-full max-w-sm shadow-xl flex flex-col max-h-[90vh]"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div class="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
        <div>
          <p class="text-sm font-bold text-navy-900">Create Person</p>
          <p class="text-xs text-slate-500 mt-0.5">Add a new person to the directory.</p>
        </div>
        <button
          type="button"
          (click)="showCreateModal.set(false)"
          class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer shrink-0 ml-3"
        >
          ✕
        </button>
      </div>

      <!-- Body -->
      <div class="overflow-y-auto px-6 pb-2 flex flex-col gap-4">
        <!-- Person Type -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Person Type</label>
          <app-select
            placeholder="Select type…"
            [options]="detailTypeOptions()"
            [value]="cPersonTypeId()"
            (changed)="cPersonTypeId.set($event)"
          />
        </div>

        <!-- First Name + Surnames -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider"
              >First Name <span class="text-red-400">*</span></label
            >
            <input
              type="text"
              class="w-full"
              placeholder="First name…"
              [value]="cFirstName()"
              (input)="cFirstName.set($any($event.target).value)"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider"
              >Surnames <span class="text-red-400">*</span></label
            >
            <input
              type="text"
              class="w-full"
              placeholder="Surnames…"
              [value]="cSurnames()"
              (input)="cSurnames.set($any($event.target).value)"
            />
          </div>
        </div>

        <!-- Phone + Sex -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Phone Number</label>
            <input
              type="tel"
              maxlength="10"
              class="w-full"
              placeholder="000-000-0000"
              [value]="cPhone()"
              (input)="cPhone.set($any($event.target).value)"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Sex</label>
            <app-select
              placeholder="Select…"
              [options]="sexOptions"
              [value]="cSex()"
              (changed)="cSex.set($event)"
            />
          </div>
        </div>

        <!-- Date of Birth -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">Date of Birth</label>
          <app-date-picker
            [value]="cDob()"
            [hideTime]="true"
            (valueChange)="cDob.set($event)"
            class="w-full"
          />
        </div>

        <!-- Medicaid CIN + HR File Number -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Medicaid CIN</label>
            <input
              type="text"
              class="w-full"
              placeholder="CIN…"
              [value]="cMedicaidCIN()"
              (input)="cMedicaidCIN.set($any($event.target).value); cCinInUse.set(false)"
              [class.border-red-400]="cCinInUse()"
              [class.ring-2]="cCinInUse()"
              [class.ring-red-100]="cCinInUse()"
            />
            @if (cCinInUse()) {
              <p class="text-2xs text-red-500">
                This CIN is already in use. Check if this record already exists.
              </p>
            }
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">HR File #</label>
            <input
              type="text"
              class="w-full"
              placeholder="HR file…"
              [value]="cHrFileNumber()"
              (input)="cHrFileNumber.set($any($event.target).value)"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 px-6 py-4 shrink-0 border-t border-surface-100 mt-2"
      >
        <button
          type="button"
          (click)="showCreateModal.set(false)"
          class="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="onCreate()"
          [disabled]="creating() || !cFirstName().trim() || !cSurnames().trim()"
          class="px-5 py-2 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ creating() ? 'Saving…' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
}
